import { useEffect, useState, useRef } from "react";
import { useOutletContext, Navigate, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Radar, ArrowUpRight, ArrowDownRight, X, Target, Check, ShieldCheck,
  ShieldAlert, Repeat, Zap, Pause, Clock,
} from "lucide-react";
import { motion } from "framer-motion";
import { ScoreGauge } from "@/components/ScoreGauge";

// Module-level cache so the Autotrade Live Activity PERSISTS across close (X) and
// reopen — it keeps running instead of resetting each time.
const _aiCache = { botId: null, latest: null, connected: true };

export default function AIActivity() {
  const { runningBot, loading } = useOutletContext();
  const navigate = useNavigate();
  const [latest, setLatest] = useState(_aiCache.latest);
  const [protLog, setProtLog] = useState([]);
  const fetchedAt = useRef(Date.now());
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!runningBot) return;
    if (_aiCache.botId !== runningBot.id) {
      _aiCache.botId = runningBot.id;
      _aiCache.latest = null;
      setLatest(null);
    }
    let on = true;
    const fetchA = async () => {
      try {
        const { data } = await api.get(`/bots/${runningBot.id}/activity`);
        if (!on) return;
        setLatest(data); _aiCache.latest = data; fetchedAt.current = Date.now();
      } catch { /* ignore */ }
      try {
        const { data } = await api.get(`/bots/${runningBot.id}/protection-log`);
        if (on) setProtLog(data.items || []);
      } catch { /* ignore */ }
    };
    fetchA();
    const poll = setInterval(fetchA, 10000);
    const ticker = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { on = false; clearInterval(poll); clearInterval(ticker); };
  }, [runningBot]);

  if (loading) {
    return (
      <div className="px-5 pt-6 pb-6 flex items-center justify-center h-[60vh] text-[#5f7ba6]" data-testid="ai-activity-loading">
        <span className="font-mono-term text-xs uppercase tracking-widest">Loading Autotrade engine…</span>
      </div>
    );
  }
  if (!runningBot) return <Navigate to="/app" replace />;

  const ex = latest?.execution || {};
  const demo = !ex.live;
  const blocked = ex.state === "blocked";
  const inTrade = ex.state === "in_trade";
  const statusColor = demo ? "#f5a623" : blocked ? "#ff5b5b" : "#22e07b";
  const statusLabel = demo ? "OFFLINE · NOT CONNECTED"
    : blocked ? "EXECUTION BLOCKED"
    : inTrade ? "LIVE · TRADING" : "LIVE · CONNECTED";
  const statusMessage = (blocked && latest?.trade_error) ? latest.trade_error : (ex.message || "");

  const prot = latest?.protection;
  const liveSecs = (base) => Math.max(0, Math.round((base || 0) - (Date.now() - fetchedAt.current) / 1000));
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const botLeft = prot ? liveSecs(prot.bot_pause_seconds_left) : 0;
  const volLeft = prot ? liveSecs(prot.vol_pause_seconds_left) : 0;
  const anyPause = prot && (botLeft > 0 || volLeft > 0 || (prot.paused_pairs || []).length > 0);

  return (
    <div className="px-5 pt-6 pb-6 fade-up space-y-4">
      {/* Top bar with close */}
      <div className="flex items-center justify-between">
        <span className="font-mono-term text-[10px] uppercase tracking-[0.3em] text-[#5f7ba6]">Autotrade Live Activity</span>
        <button onClick={() => navigate("/app")} data-testid="close-ai-activity" aria-label="Close"
          className="h-9 w-9 rounded-full border border-[#24406b] bg-[#0A1120] flex items-center justify-center text-[#8fb4e6] hover:text-white hover:bg-[#0f1a2e] transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ============ PART 1 — Autotrade status ============ */}
      <div className="rounded-3xl border border-[#14233d] bg-gradient-to-b from-[#0B1526] to-[#070c16] overflow-hidden"
        data-testid="autotrade-status-card">
        <div className="flex items-center gap-4 p-5">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center overflow-hidden shrink-0"
            style={{ backgroundColor: "var(--sfx-accent)", boxShadow: "0 0 22px var(--sfx-accent-glow)" }}>
            <img src="/smartfx-logo.png" alt="Smart Scalper Fx" className="h-11 w-11 object-contain" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full live-dot" style={{ backgroundColor: statusColor }} data-testid="robot-activated" />
              <span className="font-mono-term text-[11px] uppercase tracking-widest" style={{ color: statusColor }} data-testid="autotrade-status-label">
                {statusLabel}
              </span>
            </div>
            <p className="text-xs text-[#cfe0f5] leading-relaxed mt-1.5" data-testid="autotrade-status-message">
              {statusMessage}
            </p>
            <div className="font-mono-term text-[9px] uppercase tracking-[0.25em] text-[#3f567d] mt-2">
              Smart Scalper Fx 2V.00
            </div>
          </div>
        </div>
      </div>

      {/* ============ PART 1.5 — Loss & Volatility Protection ============ */}
      {prot && (
        <div className="rounded-3xl border bg-[#0A1120] overflow-hidden transition-colors"
          style={{ borderColor: anyPause ? "#ff8f4315" : "#14233d" }} data-testid="protection-card">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#14233d]">
            <ShieldAlert className="h-4 w-4" style={{ color: anyPause ? "#f5a623" : "var(--sfx-accent)" }} />
            <span className="font-heading font-semibold text-sm">Loss &amp; Volatility Protection</span>
            <span className={`ml-auto px-2 py-0.5 rounded-md text-[10px] font-bold ${anyPause ? "bg-[#f5a623]/15 text-[#f5a623]" : "bg-[#22e07b]/15 text-[#22e07b]"}`} data-testid="protection-status">
              {anyPause ? "PROTECTING" : "ALL CLEAR"}
            </span>
          </div>

          <div className="p-4 space-y-3">
            {botLeft > 0 && (
              <div className="rounded-2xl border border-[#ff5b5b]/30 bg-[#ff5b5b]/5 p-3" data-testid="bot-pause-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Pause className="h-4 w-4 text-[#ff8f8f]" />
                  <span className="font-heading text-xs font-semibold text-[#ff8f8f]">AutoTrade an poz (tout bòt la)</span>
                  <span className="ml-auto flex items-center gap-1 font-mono-term text-sm text-white" data-testid="bot-pause-countdown">
                    <Clock className="h-3.5 w-3.5 text-[#8fb4e6]" /> {fmt(botLeft)}
                  </span>
                </div>
                <p className="text-[11px] text-[#cfe0f5] leading-relaxed">{prot.bot_pause_reason}</p>
              </div>
            )}

            {volLeft > 0 && (
              <div className="rounded-2xl border border-[#f5a623]/30 bg-[#f5a623]/5 p-3" data-testid="vol-pause-banner">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-4 w-4 text-[#f5a623]" />
                  <span className="font-heading text-xs font-semibold text-[#f5a623]">Disjonktè volatilite aktive</span>
                  <span className="ml-auto flex items-center gap-1 font-mono-term text-sm text-white" data-testid="vol-pause-countdown">
                    <Clock className="h-3.5 w-3.5 text-[#8fb4e6]" /> {fmt(volLeft)}
                  </span>
                </div>
                <p className="text-[11px] text-[#cfe0f5] leading-relaxed">{prot.vol_pause_reason}</p>
              </div>
            )}

            {(prot.paused_pairs || []).length > 0 && (
              <div data-testid="paused-pairs">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Repeat className="h-3.5 w-3.5 text-[#8fb4e6]" />
                  <span className="font-heading text-xs font-semibold text-white">Pè an poz jiska demen (twòp pèt konsekitif)</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {prot.paused_pairs.map((p) => (
                    <span key={p} data-testid={`paused-pair-${p}`}
                      className="font-mono-term text-[11px] px-2.5 py-1 rounded-full border border-[#ff5b5b]/30 bg-[#ff5b5b]/5 text-[#ff8f8f]">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {!anyPause && (
              <p className="text-[11px] text-[#7f9bc4] leading-relaxed" data-testid="protection-clear-note">
                Okenn pwoteksyon pa deklanche. Bòt la ap veye pèt konsekitif ak volatilite ekstrèm epi l ap fè poz otomatikman pou pwoteje kapital ou.
              </p>
            )}

            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#14233d] font-mono-term text-[11px]">
              {latest?.propfirm && (
                <div>
                  <div className="text-[#5f7ba6] uppercase tracking-widest text-[9px]">Tanpon</div>
                  <div className="text-white" data-testid="prot-safety-stop">{prot.safety_stop_pct}%</div>
                </div>
              )}
              <div>
                <div className="text-[#5f7ba6] uppercase tracking-widest text-[9px]">Pèt / pè</div>
                <div className="text-white">{prot.consec_loss_per_pair}</div>
              </div>
              <div>
                <div className="text-[#5f7ba6] uppercase tracking-widest text-[9px]">Disjonktè ATR</div>
                <div className="text-white">{prot.vol_circuit_atr_pct}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ PART 1.6 — Protection Journal (history of pauses) ============ */}
      {protLog.length > 0 && (
        <div className="rounded-3xl border border-[#14233d] bg-[#0A1120] overflow-hidden" data-testid="protection-journal">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#14233d]">
            <Clock className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
            <span className="font-heading font-semibold text-sm">Jounal Pwoteksyon</span>
            <span className="ml-auto font-mono-term text-[10px] text-[#5f7ba6]">{protLog.length}</span>
          </div>
          <div className="divide-y divide-[#0f1a2e] max-h-72 overflow-y-auto">
            {protLog.map((p) => {
              const map = {
                pair_pause: { c: "#ff8f8f", t: "Pè an poz", Icon: Repeat },
                bot_pause: { c: "#ff8f8f", t: "Bòt an poz", Icon: Pause },
                volatility: { c: "#f5a623", t: "Volatilite", Icon: Zap },
                propfirm: { c: "#f5a623", t: "Prop Firm", Icon: ShieldAlert },
              };
              const m = map[p.kind] || { c: "#8fb4e6", t: p.kind, Icon: ShieldCheck };
              const M = m.Icon;
              const when = p.created_at ? new Date(p.created_at).toLocaleString([], { hour12: false }) : "";
              return (
                <div key={p.id} className="flex items-start gap-3 px-4 py-3" data-testid={`prot-log-${p.kind}`}>
                  <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: "var(--sfx-accent-soft)" }}>
                    <M className="h-3.5 w-3.5" style={{ color: m.c }} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-xs font-semibold text-white">{m.t}</span>
                      {p.symbol && <span className="font-mono-term text-[10px] text-[#8fb4e6]">{p.symbol}</span>}
                      <span className="ml-auto font-mono-term text-[9px] text-[#5f7ba6]">{when}</span>
                    </div>
                    <p className="text-[11px] text-[#cfe0f5] leading-relaxed mt-0.5">{p.reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ PART 2 — Autotrade analysis (scanner + opportunity in one place) ============ */}
      <div className="rounded-2xl border border-[#14233d] bg-[#0A1120] overflow-hidden" data-testid="autotrade-analysis">
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#14233d]">
          <div className="flex items-center gap-2">
            <Radar className="h-4 w-4" style={{ color: "var(--sfx-accent)" }} />
            <span className="font-heading font-semibold text-sm">Autotrade Analysis</span>
          </div>
          {latest && (
            <span className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">
              {latest.mode_label || latest.mode} · min {latest.threshold ?? latest.min_confidence}/100
            </span>
          )}
        </div>

        {/* Active adaptive strategy chosen for the current market condition */}
        {latest && latest.strategy && (
          <div className="px-4 py-2.5 border-b border-[#14233d] bg-[#060c18] flex items-center gap-2 flex-wrap" data-testid="active-strategy">
            {latest.regime && (
              <span className="font-mono-term text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "var(--sfx-accent-soft)", color: "var(--sfx-accent)" }}>
                {latest.regime}
              </span>
            )}
            <span className="font-heading text-xs font-semibold text-white" data-testid="active-strategy-name">{latest.strategy}</span>
            {latest.session && <span className="ml-auto font-mono-term text-[9px] text-[#5f7ba6]">{latest.session} session</span>}
          </div>
        )}

        {/* scanner list */}
        {latest && latest.scanner && latest.scanner.length > 0 && (
          <div data-testid="ai-scanner">
            {latest.scanner.map((s, i) => {
              const open = s.decision === "BUY" || s.decision === "SELL";
              const buy = s.decision === "BUY";
              return (
                <div key={s.symbol + i} data-testid={`scan-${s.symbol}`}
                  className="flex items-center gap-3 px-4 py-3 border-b border-[#0f1a2e]">
                  <span className="font-mono-term text-sm text-white w-20 shrink-0">{s.symbol}</span>
                  <div className="flex-1 min-w-0">
                    {s.score != null ? (
                      <div className="h-1.5 rounded-full bg-[#0f1a2e] overflow-hidden">
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${s.score}%`, backgroundColor: s.score >= latest.threshold ? "#22e07b" : "var(--sfx-accent)" }} />
                      </div>
                    ) : (
                      <span className="text-[11px] text-[#5f7ba6]">{s.reason}</span>
                    )}
                  </div>
                  {s.score != null && <span className="font-mono-term text-xs text-[#8fb4e6] w-10 text-right">{s.score}</span>}
                  <span data-testid={`scan-${s.symbol}-decision`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold shrink-0 w-16 justify-center ${open ? (buy ? "bg-[#22e07b]/15 text-[#22e07b]" : "bg-[#ff5b5b]/15 text-[#ff5b5b]") : "bg-[#5f7ba6]/15 text-[#8fb4e6]"}`}>
                    {open ? (buy ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />) : null}
                    {open ? s.decision : (s.decision === "SKIP" ? "SKIP" : "WAIT")}
                  </span>
                </div>
              );
            })}
            <div className="px-4 py-2 text-[10px] text-[#5f7ba6] leading-relaxed">
              Autotrade opens a trade the moment a pair's score reaches your {latest.threshold}/100 threshold. Lower Minimum Confidence in Pairs to trade more often.
            </div>
          </div>
        )}

        {/* opportunity score + confirmations + reasoning */}
        {latest && latest.confirmations && (
          <div className="p-4 border-t border-[#14233d]" data-testid="opportunity-card">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Target className="h-4 w-4 shrink-0" style={{ color: "var(--sfx-accent)" }} />
                <span className="font-heading font-semibold text-sm truncate">Trade Opportunity Score</span>
              </div>
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold shrink-0 ${latest.decision === "WAIT" ? "bg-[#f5a623]/15 text-[#f5a623]" : latest.decision === "BUY" ? "bg-[#22e07b]/15 text-[#22e07b]" : "bg-[#ff5b5b]/15 text-[#ff5b5b]"}`} data-testid="ai-decision">
                {latest.decision === "WAIT" ? "WAITING" : latest.decision}
              </span>
            </div>

            <div className="flex items-center gap-4 mt-3">
              <ScoreGauge score={latest.opportunity_score} threshold={latest.min_confidence}
                decision={latest.decision} />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="font-mono-term text-[10px] uppercase tracking-widest text-[#5f7ba6]">
                  {latest.mode_label}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-lg font-bold text-white">{latest.checks_passed}</span>
                  <span className="text-[#5f7ba6] text-xs">/ {latest.checks_total} confirmations</span>
                </div>
                <div className="h-1.5 rounded-full bg-[#060c18] overflow-hidden">
                  <motion.div className="h-full rounded-full"
                    style={{ backgroundColor: latest.opportunity_score >= 80 ? "#22e07b" : latest.opportunity_score >= 65 ? "var(--sfx-accent)" : "#f5a623" }}
                    initial={{ width: 0 }}
                    animate={{ width: `${(latest.checks_passed / Math.max(1, latest.checks_total)) * 100}%` }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }} />
                </div>
                <p className="text-[11px] leading-snug"
                  style={{ color: latest.opportunity_score >= latest.min_confidence ? "#22e07b" : "#7f9bc4" }}>
                  {latest.opportunity_score >= latest.min_confidence
                    ? "Score met your threshold — trade ready."
                    : `Needs ${Math.max(0, latest.min_confidence - latest.opportunity_score)} more points to fire.`}
                </p>
              </div>
            </div>

            <div className="mt-3 space-y-1.5" data-testid="confirmations-list">
              {latest.confirmations.map((c) => (
                <div key={c.name} className="flex items-center gap-2" data-testid={`confirmation-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                  <span className={`h-4 w-4 rounded-full flex items-center justify-center shrink-0 ${c.passed ? "bg-[#22e07b]/20 text-[#22e07b]" : "bg-[#ff5b5b]/15 text-[#ff5b5b]"}`}>
                    {c.passed ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}
                  </span>
                  <span className="font-heading text-xs font-semibold text-white shrink-0 w-[112px]">{c.name}</span>
                  <span className="text-[11px] text-[#7f9bc4] truncate">{c.detail}</span>
                </div>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-[#14233d]">
              <p className="text-xs text-[#cfe0f5] leading-relaxed" data-testid="ai-reasoning">{latest.decision_reason}</p>
            </div>

            {/* Prop Firm protection (only for prop-firm accounts) */}
            {latest.propfirm && (
              <div className="mt-3 pt-3 border-t border-[#14233d]" data-testid="propfirm-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4" style={{ color: latest.propfirm.blocked ? "#ff5b5b" : "var(--sfx-accent)" }} />
                    <span className="font-heading font-semibold text-sm">Prop Firm Protection · {latest.propfirm.phase_label}</span>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${latest.propfirm.blocked ? "bg-[#ff5b5b]/15 text-[#ff5b5b]" : "bg-[#22e07b]/15 text-[#22e07b]"}`} data-testid="propfirm-status">
                    {latest.propfirm.blocked ? "BLOCKED" : "PROTECTED"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 font-mono-term text-[11px]">
                  <div><div className="text-[#5f7ba6] uppercase tracking-widest text-[9px]">Daily Loss</div><div className="text-white">${(latest.propfirm.snapshot?.daily_loss || 0).toLocaleString()} / ${(latest.propfirm.daily_loss_limit || 0).toLocaleString()}</div></div>
                  <div><div className="text-[#5f7ba6] uppercase tracking-widest text-[9px]">Drawdown</div><div className="text-white">${(latest.propfirm.snapshot?.drawdown || 0).toLocaleString()} / ${(latest.propfirm.max_drawdown || 0).toLocaleString()}</div></div>
                  <div><div className="text-[#5f7ba6] uppercase tracking-widest text-[9px]">Profit</div><div className={latest.propfirm.snapshot?.profit >= 0 ? "text-[#22e07b]" : "text-[#ff5b5b]"}>${(latest.propfirm.snapshot?.profit || 0).toLocaleString()}</div></div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
